import React, { Component, createRef } from 'react'
import { toast } from 'react-toastify'
import { Form, Col, InputGroup, Button, Spinner } from 'react-bootstrap'
import TextFormControl from './TextFormControl'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import FormatPhone from '../../formatters/FormatPhone'
import FormatSSN from '../../formatters/FormatSSN'
import { DonorInformationModel, GetRegulatoryModeDonor } from './OrderModels'
import { Subscription } from "rxjs"
import { StateObservable } from "../../api/OrderRequests/OrderRequestsConstraintGet"
import State from "../../api/OrderRequests/OrderRequestsModel/State"
import StateSelect from "../FormattedInputs/StateSelect"
import { Donor } from "../../api/OrderRequests/OrderRequestsModel/Donor"
import { SearchDonorGetObservable } from "../../api/OrderRequests/SearchDonorGet"
import { FaSearch } from "react-icons/fa"
import { BirthdayInput } from "../FormattedInputs/BirthdayInput"
import { DonorData } from './OrderRequest'
import { DonorIdRegexp, InvalidDonorId, InvalidDonorIdLength, InvalidNameMessage, MaxDonorIdLength, NameRegexp } from '../../config/Constants';
import { DonorDetail } from "../../api/OrderRequests/SSOOrderRequest"
import RegulatoryMode from '../../api/LabService/LabServiceModel/RegulatoryMode'

type PhoneField = "WorkPhone" | "CellPhone" | "HomePhone"

export interface DonorInformationProps {
  companyId?: number
  completingPendingOrders?: boolean
  orderFormData?: DonorData
  donor: DonorInformationModel
  idTypes: GetRegulatoryModeDonor[]
  isBirthDateRequired?: boolean
  regulatoryMode?: RegulatoryMode
  showIdType: boolean
  showMiddleInitial: boolean
  eveningPhoneLabel: string
  daytimePhoneLabel: string
  eveningPhoneField: PhoneField
  daytimePhoneField: PhoneField
  daytimePhoneRequired: boolean
  showGenderField: boolean
  validateDonorIdFormat?: boolean
  maxDonorIdLength?: number
  SSODonorData?: DonorDetail
  onDonorChange: (donor: DonorInformationModel, isValid: boolean, isDonorInfoSuccess: boolean) => void
}

interface DonorInformationState extends DonorInformationModel {
  loading: boolean
  statesLoading: boolean
}

export default class DonorInformation extends Component<DonorInformationProps, DonorInformationState> {
  state: DonorInformationState = {
    ...this.props.donor,
    idTypeObj: this.props.donor.idType && this.props.idTypes.find(item => item.PrimaryIDType === this.props.donor.idType) ? this.props.donor.idTypeObj : this.props.idTypes.sort((a, b) => (a.Rank < b.Rank ? -1 : 1))[0],
    loading: false,
    statesLoading: false
  }
  birthDateInput = createRef<DatePicker>()
  isDonorInfoSuccess: boolean = false
  lastSearch: string = ""

  subscriptions = new Subscription()

  componentDidMount() {
    this.handleDonorIdChange(this.state.donorId || "")
  }

  componentWillUnmount() {
    this.subscriptions && this.subscriptions.unsubscribe()
  }

  componentDidUpdate(prevProps: DonorInformationProps) {
    const objEq = (obj1: GetRegulatoryModeDonor[], obj2: GetRegulatoryModeDonor[]) => obj1.every((val, index) => val === obj2[index])
    if (!prevProps.idTypes.length && this.props.idTypes.length || prevProps.idTypes.length && !objEq(prevProps.idTypes, this.props.idTypes)) {
      if (this.props.completingPendingOrders && this.props.orderFormData) {
        this.fillPendingFormData(this.props.orderFormData)
      }
      else {
        this.handleIdTypeChange(this.props.donor.idType)
        this.populateSSODonorData()
      }
    }
  }

  fillPendingFormData(data: DonorData) {
    const idType = data.IDType
    const idTypeObj = this.props.idTypes.find(item => item.PrimaryIDType === idType) || this.props.idTypes[0]
    if (this.displayState(idTypeObj)) {
      this.loadStates()
    }
    this.setState({
      personId: data.PersonID,
      firstName: data.FirstName,
      lastName: data.LastName,
      donorId: data.DonorID,
      idType: data.IDType,
      idTypeObj: idTypeObj,
    })
    SearchDonorGetObservable({
      CompanyID: this.props.companyId || 0,
      DonorID: data.MockDonorID,
      Identifier: idType,
      IdentifierType: idTypeObj.Rank,
      RegulatoryModeID: this.props.regulatoryMode?.RegulatoryModeID || 0,
    }).subscribe(
      response => this.getDonorInfoSuccess(response),
      error => this.getDonorError(error)
    )
  }

  loadStates = () => {
    if (!this.state.states && !this.state.statesLoading) {
      this.setState({ statesLoading: true })
      this.subscriptions.add(
        StateObservable().subscribe(
          (results: State[]) => this.setState({ states: results, statesLoading: false }),
          () => { this.setState({ statesLoading: false }) }
        )
      )
    }
  }

  populateSSODonorData = () => {
    const data = this.props.SSODonorData
    if (!data) return

    this.handleIdTypeChange(data.DonorIDType)
    if (data.DonorID) {
      this.handleDonorIdChange(data.DonorID)
    }
    this.setState({
      idReadOnly: !!data.DonorID,
      firstName: data.DonorFirstName,
      lastName: data.DonorLastName,
      donorId: data.DonorID,
      idType: data.DonorIDType,
      birthdate: data.DonorDOB ? new Date(data.DonorDOB) : undefined,
      daytimePhone: data.DonorPhone,
      email: data.DonorEmail,
      address: { Address1: data.Address?.Line1 || "", Address2: data.Address?.Line2 || "", City: data.Address?.City || "", State: data.Address?.State || "", Zip: data.Address?.Zip || "" }
    }, () => this.onDonorSearch())
  }

  handleIdTypeChange = (idType?: string) => {
    const idTypeObject = this.props.idTypes.find(item => item.PrimaryIDType === idType) || this.props.idTypes.sort((a, b) => (a.Rank < b.Rank ? -1 : 1))[0]

    if (this.displayState(idTypeObject)) {
      this.loadStates()
    }

    this.updateState({ donorId: "", idTypeObj: idTypeObject }, true)
  }

  handleDonorIdChange = (value: string) => {
    value = this.state.idTypeObj?.PrimaryIDType === "SSN" ? FormatSSN(value) : value
    this.updateState({ donorId: value })
  }

  handleStateChange = (state: State) => this.updateState({ state: state, stateCode: state?.Abbreviation }, true)
  handleBirthDateChange = (value: Date | undefined) => this.updateState({ birthdate: value }, true)
  handleChange = (name: string, value: string) => this.updateState({ [name]: value })
  isPhoneValid = (value: string | undefined, require: boolean) => (require === false && !value) || (value || '').replace(/\D/g, "").length === 10 || value === "n/p" || value === "N/P" || value === "np" || value === "NP"
  isFirstNameValid = (value: string | undefined, require: boolean) => (require === false && !value) || ((value || '').trim().length > 0 && NameRegexp.test((value || '').trim()))
  isLastNameValid = (value: string | undefined, require: boolean) => (require === false && !value) || ((value || '').trim().length > 0 && NameRegexp.test((value || '').trim()))
  isSSNValid = (value: string) => this.props.completingPendingOrders || value.replace(/\D/g, "").length === 9

  updateState(changes: object, sendDonorInformation: boolean = false) {
    this.setState(changes, () => {
      if (sendDonorInformation) {
        const newInfo = { ...this.state, ...changes }
        this.sendDonorInformation(newInfo)
      }
    })
  }

  onDonorSearch = () => {
    const identifier = (this.state.donorId?.toString().replace(/-/g, "") || "").trim()
    if(identifier.length === 0){
      return
    }
    if (identifier === this.lastSearch) {
      return
    }
    this.lastSearch = identifier

    if (this.state.idTypeObj?.PrimaryIDType === "SSN" && !this.isSSNValid(this.state.donorId || "")) {
      toast.error("Please enter valid 9 digit ID.")
      return
    }

    if (!this.props.companyId) {
      return
    }

    this.setState({ loading: true })
    SearchDonorGetObservable({ CompanyID: this.props.companyId || 0, Identifier: identifier, IdentifierType: this.state.idTypeObj?.Rank || 0, RegulatoryModeID: this.props.regulatoryMode?.RegulatoryModeID || 0 }).subscribe(
      response => this.getDonorInfoSuccess(response),
      error => this.getDonorError(error)
    )
  }

  getDonorInfoSuccess = (response: Donor[]) => {
    if (response.length === 0) {
      this.isDonorInfoSuccess = false
      toast.error("Donor was not found. New donor will be created.")
      if (this.props.SSODonorData) {
        this.setState({
          loading: false,
          firstNameReadOnly: false,
          middleInitialReadOnly: false,
          lastNameReadOnly: false,
          homePhoneReadOnly: false,
          workPhoneReadOnly: false,
          stateReadOnly: false,
          genderReadOnly: false,
          idReadOnly: true
        })
      } else {
      this.setState(this.props.completingPendingOrders ? {
        loading: false,
        firstNameReadOnly: !!this.state.firstName,
        middleInitialReadOnly: !!this.state.middleInitial,
        lastNameReadOnly: !!this.state.lastName,
        homePhoneReadOnly: (this.state.eveningPhone || "") !== "",
        workPhoneReadOnly: (this.state.daytimePhone || "") !== "",
        stateReadOnly: (this.state.stateCode || "") !== "",
        genderReadOnly: !!this.state.gender
      } : {
        loading: false,
        firstName: "",
        middleInitial: "",
        lastName: "",
        eveningPhone: "",
        daytimePhone: "",
        birthdate: undefined,
        firstNameReadOnly: false,
        middleInitialReadOnly: false,
        lastNameReadOnly: false,
        homePhoneReadOnly: false,
        workPhoneReadOnly: false,
        state: undefined,
        stateCode: undefined,
        personId: undefined,
        accessCode: undefined,
        gender: "",
        email: "",
        genderReadOnly: false
      })
      }
    } else if (this.props.SSODonorData) {
      this.isDonorInfoSuccess = true
      const data = response[0]
      const fetchedDate = data.DateOfBirth ? new Date(data.DateOfBirth) : undefined
      const validDate = fetchedDate && fetchedDate.getTime && !isNaN(fetchedDate.getTime())
      const { firstName, middleInitial, lastName, eveningPhone, daytimePhone, birthdate } = this.state
      let newData = { firstName: firstName , middleInitial: middleInitial, lastName: lastName, eveningPhone: eveningPhone, daytimePhone: daytimePhone, birthdate: birthdate }
      if (this.noValueOrEmpty(firstName)) newData.firstName = data.FirstName
      if (this.noValueOrEmpty(middleInitial)) newData.middleInitial = data.MiddleInitial
      if (this.noValueOrEmpty(lastName)) newData.lastName = data.LastName
      if (this.noValueOrEmpty(eveningPhone)) newData.eveningPhone = FormatPhone(data[this.props.eveningPhoneField] || '')
      if (this.noValueOrEmpty(daytimePhone)) newData.daytimePhone = FormatPhone(data[this.props.daytimePhoneField] || '')
      if (this.noValueOrEmpty(birthdate)) newData.birthdate = validDate ? fetchedDate : undefined
      this.setState({ loading: false, personId: data.PersonID, ...newData })
    }
    else {
      this.isDonorInfoSuccess = true
      const data = response[0]
      const fetchedDate = data.DateOfBirth ? new Date(data.DateOfBirth) : undefined
      const validDate = fetchedDate && fetchedDate.getTime && !isNaN(fetchedDate.getTime())

      this.setState({
        loading: false,
        personId: data.PersonID,
        firstName: data.FirstName,
        middleInitial: data.MiddleInitial.substring(0,1),
        lastName: data.LastName,
        eveningPhone: FormatPhone(data[this.props.eveningPhoneField] || ''),
        daytimePhone: FormatPhone(data[this.props.daytimePhoneField] || ''),
        birthdate: validDate ? fetchedDate : undefined,
        accessCode: data.GovAccessCode,
        gender: data.Gender,
        email: data.Email,
        firstNameReadOnly: !!data.FirstName,
        middleInitialReadOnly: !!data.MiddleInitial,
        lastNameReadOnly: !!data.LastName,
        homePhoneReadOnly: (data.HomePhone || "") !== "N/P",
        workPhoneReadOnly: (data.WorkPhone || "") !== "N/P",
        stateReadOnly: (data.DriverLICStateOfIssue !== ""),
        genderReadOnly: !!data.Gender,
        state: undefined,
        stateCode: data.DriverLICStateOfIssue ? data.DriverLICStateOfIssue : undefined
      })
    }
    this.sendDonorInformation()
  }

  noValueOrEmpty = (value: string | Date | undefined) => !value || value === ""

  getDonorError = (error: string): void => {
    this.isDonorInfoSuccess = false
    this.setState({ loading: false })
    toast.error("Donor was not found. New donor will be created.")
  }

  isDonorIdValid = (info: DonorInformationModel = this.state) => {
    const { donorId, idTypeObj } = info
    if (donorId === undefined) { return true }
    if (idTypeObj?.PrimaryIDType === "SSN") {
      return this.isSSNValid(donorId)
    }
    const value = donorId.trim()
    return value.length > 0 && value.length <= MaxDonorIdLength && (this.props.validateDonorIdFormat ? DonorIdRegexp.test(value) : true)
  }

  sendDonorInformation = (info: DonorInformationModel = this.state) => {
    const { firstName, lastName, eveningPhone, daytimePhone, birthdate, gender } = info
    const isFormValid = this.isDonorIdValid(info) &&
      this.isStateValid(info) &&
      this.isPhoneValid(eveningPhone, false) &&
      this.isPhoneValid(daytimePhone, this.props.daytimePhoneRequired) &&
      this.isFirstNameValid(firstName, true) &&
      this.isLastNameValid(lastName, true) &&
      (this.props.isBirthDateRequired ? !!birthdate : true) &&
      ((gender || "").trim().length > 0 || !this.props.showGenderField)

    this.props.onDonorChange(info, isFormValid, this.isDonorInfoSuccess)
  }

  displayState = (idType?: GetRegulatoryModeDonor) => idType?.PrimaryIDType === "DriverID"
  isStateValid = (info: DonorInformationModel = this.state) => !this.displayState(info.idTypeObj) || !!info.stateCode

  render() {
    const { idTypeObj, donorId, firstName, middleInitial, lastName, eveningPhone, daytimePhone, birthdate, gender, statesLoading } = this.state
    const { showIdType, showMiddleInitial, eveningPhoneLabel, daytimePhoneLabel, daytimePhoneRequired, showGenderField, validateDonorIdFormat } = this.props
    const validDonorIdFormat = validateDonorIdFormat ? DonorIdRegexp.test(donorId || "") : true

    const label = (text: string, required: boolean) => <Form.Label className={`font-weight-bold${required ? ' required' : ''}`}>{text}</Form.Label>
    const idTypesOptions = () => this.props.idTypes.sort((a, b) => (a.Rank > b.Rank) ? 1 : -1).map(item => <option value={item.PrimaryIDType} key={item.PrimaryIDType}>{item.Description || item.PrimaryIDType}</option>)
    return (
      <Form>
        <Form.Row>
          <Form.Label column className="font-weight-bold required">DONOR INFORMATION</Form.Label>
        </Form.Row>
        <Form.Row>
          {showIdType && <Col xs={12} sm={4}>
            <Form.Group>
              {label('ID TYPE', false)}
              <Form.Control as="select"
                disabled={this.props.completingPendingOrders}
                readOnly={this.state.idReadOnly}
                value={idTypeObj?.PrimaryIDType}
                onChange={e => this.handleIdTypeChange(e.target.value)}
                onBlur={() => this.sendDonorInformation}>
                {idTypesOptions()}
              </Form.Control>
            </Form.Group>
          </Col>}
          <Col xs={12} sm={4}>
            <Form.Group>
              {label('DONOR ID', true)}
              <InputGroup className="mb-3">
                <TextFormControl value={donorId}
                  readOnly={this.state.idReadOnly}
                  disabled={this.props.completingPendingOrders}
                  maxLength={this.props.maxDonorIdLength}
                  changeHandler={(value) => this.handleDonorIdChange(value)}
                  isValid={() => this.props.completingPendingOrders || this.isDonorIdValid()}
                  blurHandler={() => { this.sendDonorInformation(); this.onDonorSearch(); }}
                  enterTapHandler={this.onDonorSearch} />
                <Form.Control.Feedback className="order-last" type="invalid">{!validDonorIdFormat ? InvalidDonorId : InvalidDonorIdLength}</Form.Control.Feedback>
                <InputGroup.Append>
                  <Button className="btn-rounded-right" onClick={() => this.onDonorSearch()} disabled={!donorId || this.props.completingPendingOrders}>
                    {this.state.loading ? <Spinner size="sm" animation="border" /> : <FaSearch />}
                  </Button>
                </InputGroup.Append>
              </InputGroup>
            </Form.Group>
          </Col>
          <Col xs={12} sm={4}>
            <Form.Group hidden={!this.displayState(idTypeObj)}>
              {label('STATE OF ISSUANCE', true)}
              <StateSelect
                isInvalid={!this.isStateValid()}
                states={this.state.states}
                dropButtonIfInvalid
                selected={this.state.state}
                selectedCode={this.state.stateCode}
                onChange={(state) => this.handleStateChange(state)} />
            </Form.Group>
          </Col>
          {!showIdType && <Col xs={12} sm={4}></Col>}
          <Col xs={12} sm={4}>
            <Form.Group>
              {label('FIRST NAME', true)}
              <TextFormControl value={firstName}
                //maxLength={20}
                readOnly={this.state.firstNameReadOnly}
                changeHandler={(value) => this.handleChange('firstName', value)}
                isValid={() => this.isFirstNameValid(firstName, true)}
                blurHandler={this.sendDonorInformation} />
              <Form.Control.Feedback type="invalid">{InvalidNameMessage}</Form.Control.Feedback>
            </Form.Group>
          </Col>
          {showMiddleInitial && <Col xs={12} sm={4}>
            <Form.Group>
              {label('MIDDLE INITIAL', false)}
              <TextFormControl value={middleInitial}
                maxLength={1}
                readOnly={this.state.middleInitialReadOnly || this.props.completingPendingOrders}
                changeHandler={(value) => this.handleChange('middleInitial', value)}
                blurHandler={this.sendDonorInformation} />
            </Form.Group>
          </Col>}
          <Col xs={12} sm={4}>
            <Form.Group>
              {label('LAST NAME', true)}
              <TextFormControl value={lastName}
                //maxLength={20}
                readOnly={this.state.lastNameReadOnly}
                changeHandler={(value) => this.handleChange('lastName', value)}
                isValid={() => this.isFirstNameValid(lastName, true)}
                blurHandler={this.sendDonorInformation} />
              <Form.Control.Feedback type="invalid">{InvalidNameMessage}</Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col xs={12} sm={6}>
            <Form.Group>
              {label(eveningPhoneLabel, false)}
              <TextFormControl value={eveningPhone}
                readOnly={this.state.homePhoneReadOnly || this.props.completingPendingOrders}
                changeHandler={(value) => this.handleChange('eveningPhone', value)}
                formatFunction={(value) => FormatPhone(value)}
                isValid={() => this.isPhoneValid(eveningPhone, false)}
                blurHandler={this.sendDonorInformation} />
            </Form.Group>
          </Col>
          <Col xs={12} sm={6}>
            <Form.Group>
              {label(daytimePhoneLabel, daytimePhoneRequired)}
              <TextFormControl value={daytimePhone}
                readOnly={this.state.workPhoneReadOnly}
                changeHandler={(value) => this.handleChange('daytimePhone', value)}
                formatFunction={(value) => FormatPhone(value)}
                isValid={() => this.isPhoneValid(daytimePhone, daytimePhoneRequired)}
                blurHandler={this.sendDonorInformation} />
            </Form.Group>
          </Col>
          <Col xs={12} sm={6}>
            <Form.Group>
              {label("DATE OF BIRTH", !!this.props.isBirthDateRequired)}
              <DatePicker className="form-control"
                ref={this.birthDateInput}
                required={this.props.isBirthDateRequired}
                peekNextMonth
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                maxDate={new Date()}
                selected={birthdate}
                enableTabLoop={false}
                onKeyDown={(e) => { if (this.birthDateInput.current && (e.key === 'Tab')) { this.birthDateInput.current.setOpen(false) } }}
                onChange={date => this.handleBirthDateChange(date as Date || undefined)}
                customInput={<BirthdayInput required={this.props.isBirthDateRequired} />} />
            </Form.Group>
          </Col>
          {showGenderField &&
            <Col xs={12} sm={6}>
              <Form.Group>
                {label("GENDER", true)}
                <Form.Control id="gender" value={gender} as="select"
                  disabled={this.state.genderReadOnly || this.props.completingPendingOrders}
                  required
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => this.updateState({ gender: e.target.value }, true)}>
                  <option value="">Select</option>
                  <option value="M">Male</option>
                  <option value="F">Female</option>
                </Form.Control>
              </Form.Group>
            </Col>
          }
        </Form.Row>
      </Form>
    )
  }

  static defaultProps = {
    showIdType: true,
    showMiddleInitial: true,
    eveningPhoneLabel: 'EVENING PHONE',
    daytimePhoneLabel: 'DAYTIME PHONE',
    eveningPhoneField: 'HomePhone',
    daytimePhoneField: 'WorkPhone',
    daytimePhoneRequired: true,
    showGenderField: false
  }

}
